import cropImage from './image_crop';
import imageControls, {alignmentButtons, alignments} from './blog_image_controls';

const t = key => I18n.t(`webshop.blog_images.${key}`);
const types = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
export function imageLink(value) {
  const text = (value || '').trim();
  if (!text) return '';
  if (/^\/(?!\/)/.test(text) && !/[\\\s]/.test(text)) return text;
  const url = new URL(text);
  if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password) throw new Error('link');
  return url.href;
}

// Use DOM attributes rather than HTML interpolation for user-supplied links/text.
function imageWidth(value) {
  const text = String(value ?? '').trim();
  if (!text) return null;
  if (!/^\d+$/.test(text) || Number(text) < 1 || Number(text) > 10000) throw new Error('width');
  return Number(text);
}

export function linkedImageHTML(url, alt, link, width, alignment = 'center', caption = '') {
  const image = document.createElement('img'); image.src = url; image.alt = alt || '';
  const size = imageWidth(width);
  if (size) image.setAttribute('width', size);
  image.style.maxWidth = '100%'; image.style.height = 'auto';
  const wrapper = document.createElement('span');
  wrapper.className = `blog-inline-image blog-image-${alignments.includes(alignment) ? alignment : 'center'}`;
  if (link) {
    const anchor = document.createElement('a'); anchor.href = imageLink(link);
    anchor.append(image); wrapper.append(anchor);
  } else wrapper.append(image);
  if (caption) {
    const label = document.createElement('span'); label.className = 'blog-image-caption'; label.textContent = caption;
    wrapper.append(label);
  }
  return wrapper.outerHTML;
}

function imageDetails(attachment, editor) {
  const attributes = attachment.getAttributes();
  const fragment = document.createElement('div'); fragment.innerHTML = attributes.content || '';
  const image = fragment.querySelector('img');
  if (image) return {url: image.getAttribute('src'), alt: image.alt, link: image.closest('a')?.getAttribute('href') || '', width: image.getAttribute('width') || '',
    alignment: alignments.find(value => image.closest(`.blog-image-${value}`)) || 'center', caption: fragment.querySelector('.blog-image-caption')?.textContent || ''};
  if ((attributes.contentType === 'image' || attributes.contentType?.startsWith('image/')) && attributes.url) {
    const figure = editor?.querySelector(`[data-trix-id="${attachment.id}"]`);
    const caption = attributes.caption || JSON.parse(figure?.dataset.trixAttributes || '{}').caption || '';
    return {url: attributes.url, alt: caption, caption, link: attributes.href === attributes.url ? '' : attributes.href || '', width: attributes.width || '', alignment: 'center'};
  }
  return null;
}

function button(label, action) {
  const element = document.createElement('button'); element.type = 'button';
  element.className = 'btn btn-outline-secondary btn-sm'; element.textContent = t(label);
  element.addEventListener('click', action); return element;
}

async function editImage(options) {
  const dialog = document.createElement('dialog'); dialog.className = 'rounded border p-3';
  dialog.style.cssText = 'width:720px;max-width:95vw;max-height:95vh;overflow:auto';
  const heading = document.createElement('h2'); heading.className = 'fs-5'; heading.textContent = t('title');
  const input = document.createElement('input'); input.type = 'file'; input.accept = types.join(','); input.className = 'form-control mb-3'; input.setAttribute('aria-label', t('choose'));
  const preview = document.createElement('img'); preview.className = 'img-fluid rounded mb-3'; preview.style.maxHeight = '320px'; preview.alt = '';
  let file, objectURL, source = options.url || '';
  const animated = () => file?.type === 'image/gif' || /\.gif(?:\?|$)/i.test(source);
  preview.hidden = !source; if (source) preview.src = source;
  const fields = {};
  const field = (key, value) => {
    const label = document.createElement('label'); label.className = 'form-label d-block'; label.textContent = t(key);
    const control = document.createElement('input'); control.className = 'form-control mb-3'; control.value = value || ''; control.maxLength = key === 'alt' ? 500 : 2000;
    label.append(control); fields[key] = control; return label;
  };
  const widthField = field('width', options.width);
  fields.width.type = 'number'; fields.width.min = '1'; fields.width.max = '10000'; fields.width.step = '1';
  fields.width.placeholder = t('width_auto');
  let alignment = options.alignment || 'center';
  const alignmentField = document.createElement('div'); alignmentField.className = 'mb-3';
  if (options.inline) {
    const label = document.createElement('div'); label.className = 'form-label'; label.textContent = t('alignment');
    alignmentField.append(label, alignmentButtons(alignment, value => alignment = value));
  }
  const error = document.createElement('p'); error.className = 'text-danger'; error.setAttribute('role', 'alert');
  const setFile = selected => {
    if (!types.includes(selected.type) || selected.size > 10 * 1024 * 1024) throw new Error('file');
    file = selected; if (objectURL) URL.revokeObjectURL(objectURL);
    source = objectURL = URL.createObjectURL(file); preview.src = source; preview.hidden = false;
    crop.disabled = animated(); error.textContent = '';
  };
  input.addEventListener('change', () => {
    try { if (input.files[0]) setFile(input.files[0]); } catch (_) { error.textContent = t('error'); }
  });
  const crop = button('crop', async () => {
    if (!source) { error.textContent = t('choose'); return; }
    try { await cropImage(source, file?.name || 'blog-image.png', setFile, key => t(key)); }
    catch (_) { error.textContent = t('crop_error'); }
  });
  crop.disabled = animated();
  const apply = button('apply', async () => {
    if (apply.disabled) return;
    apply.disabled = true; input.disabled = true; crop.disabled = true; error.textContent = '';
    try {
      const link = options.simple ? '' : imageLink(fields.link.value);
      const width = options.simple ? null : imageWidth(fields.width.value);
      if (!source) throw new Error('file');
      let url = options.url;
      if (file && options.upload) url = await options.upload(file);
      await options.apply({file, url, link, alt: fields.alt.value.trim(), width, alignment});
      dialog.close();
    } catch (_) { error.textContent = t('error'); }
    finally { apply.disabled = false; input.disabled = false; crop.disabled = animated(); }
  });
  apply.className = 'btn btn-primary';
  const actions = document.createElement('div'); actions.className = 'd-flex gap-2';
  actions.append(crop, button('cancel', () => dialog.close()), apply);
  const help = document.createElement('p'); help.className = 'form-text';
  help.textContent = options.simple ? I18n.t('webshop.page_editor.image_help') : t('help');
  dialog.append(heading, input, preview);
  if (!options.simple) dialog.append(widthField, alignmentField);
  dialog.append(field('alt', options.alt));
  if (!options.simple) dialog.append(field('link', options.link));
  dialog.append(help, actions, error);
  document.body.append(dialog);
  dialog.addEventListener('close', () => { if (objectURL) URL.revokeObjectURL(objectURL); dialog.remove(); }, {once: true});
  // Bootstrap's focus trap must yield to the native modal dialog.
  dialog.addEventListener('focusin', event => event.stopPropagation());
  dialog.showModal();
}

export function installPageImages(root, controller, data = {}) {
  installBlogImages(root, controller, data);
  if (root.dataset.pageImagesInstalled) return;
  root.dataset.pageImagesInstalled = 'true';
  // This is one page-wide setting shown next to each translated page title.
  root.querySelectorAll('[data-page-show-title]').forEach(checkbox => {
    checkbox.addEventListener('change', () => {
      root.querySelectorAll('[data-page-show-title]').forEach(other => { other.checked = checkbox.checked; });
    });
  });
  const cover = root.querySelector('#page_image');
  if (!cover) return;
  cover.accept = types.join(',');
  const alt = document.createElement('input'); alt.id = 'page_image_alt';
  alt.className = 'form-control mt-2'; alt.maxLength = 500; alt.value = data.data?.image_alt || '';
  const label = document.createElement('label'); label.className = 'form-label mt-2';
  label.htmlFor = alt.id; label.textContent = t('alt');
  const current = document.createElement('img'); current.className = 'img-thumbnail mt-2';
  current.style.maxWidth = '240px'; current.alt = '';
  let objectURL;
  const previewFile = file => {
    if (objectURL) URL.revokeObjectURL(objectURL);
    current.src = objectURL = URL.createObjectURL(file); current.hidden = false;
  };
  current.hidden = !data.image; if (data.image) current.src = data.image;
  const edit = button('title', () => editImage({simple: true, url: current.hidden ? '' : current.src, alt: alt.value,
    apply: result => {
      if (result.file) {
        const files = new DataTransfer(); files.items.add(result.file); cover.files = files.files;
        previewFile(result.file);
      }
      alt.value = result.alt;
    }}));
  cover.after(label, alt, current, edit);
  cover.addEventListener('change', () => { if (cover.files[0]) previewFile(cover.files[0]); });
  root.addEventListener('hidden.bs.modal', () => { if (objectURL) URL.revokeObjectURL(objectURL); }, {once: true});
}

export default function installBlogImages(root, controller, data = {}) {
  if (root.dataset.blogImagesInstalled) return;
  root.dataset.blogImagesInstalled = 'true';
  const webshop = controller.uuid;
  root.dataset.blogWebshop = webshop;
  const cover = root.querySelector('#blog_image');
  if (cover) {
    cover.accept = types.join(',');
    const link = document.createElement('input'); link.id = 'blog_image_link'; link.className = 'form-control mt-2'; link.maxLength = 2000;
    link.value = data.data?.image_link || '';
    const label = document.createElement('label'); label.className = 'form-label mt-2'; label.htmlFor = link.id; label.textContent = t('link');
    const alt = document.createElement('input'); alt.id = 'blog_image_alt'; alt.className = 'form-control mt-2'; alt.maxLength = 500; alt.value = data.data?.image_alt || '';
    const altLabel = document.createElement('label'); altLabel.className = 'form-label mt-2'; altLabel.htmlFor = alt.id; altLabel.textContent = t('alt');
    const width = document.createElement('input'); width.id = 'blog_image_width'; width.type = 'number'; width.min = '1'; width.max = '10000'; width.step = '1';
    width.className = 'form-control mt-2'; width.value = data.data?.image_width || ''; width.placeholder = t('width_auto');
    const widthLabel = document.createElement('label'); widthLabel.className = 'form-label mt-2'; widthLabel.htmlFor = width.id; widthLabel.textContent = t('width');
    const current = document.createElement('img'); current.className = 'img-thumbnail mt-2'; current.style.maxWidth = '240px'; current.alt = '';
    current.hidden = !data.image; if (data.image) current.src = data.image;
    const edit = button('title', () => editImage({url: current.hidden ? '' : current.src, link: link.value, alt: alt.value, width: width.value,
      apply: result => {
        if (result.file) { const files = new DataTransfer(); files.items.add(result.file); cover.files = files.files; current.src = URL.createObjectURL(result.file); }
        current.hidden = false; link.value = result.link; alt.value = result.alt; width.value = result.width || '';
      }}));
    cover.after(widthLabel, width, altLabel, alt, label, link, current, edit);
    cover.addEventListener('change', () => { if (cover.files[0]) { current.src = URL.createObjectURL(cover.files[0]); current.hidden = false; } });
  }
  const upload = file => new Promise((resolve, reject) => {
    const form = new FormData(); form.append('file', file); form.append('webshop', webshop);
    Rails.ajax({url: `/${controller.locale}/blog/upload`, type: 'post', data: form,
      success: result => result.success ? resolve(result.direct_url) : reject(new Error('upload')),
      error: reject});
  });
  root.querySelectorAll('trix-editor').forEach(editor => {
    const list = document.createElement('div'); list.className = 'd-flex flex-wrap gap-2 mt-2';
    const details = attachment => imageDetails(attachment, editor);
    const replace = (attachment, result, range = editor.editor.getSelectedRange()) => {
      // Replace the attachment instead of mutating it: undo snapshots retain the previous image.
      editor.editor.recordUndoEntry(t('edit'));
      const caption = attachment ? details(attachment)?.caption : '';
      const attributes = {content: linkedImageHTML(result.url, result.alt, result.link, result.width, result.alignment, caption), contentType: 'text/html'};
      const replacement = new window.Trix.Attachment(attributes);
      if (attachment) {
        const position = editor.editor.getDocument().getRangeOfAttachment(attachment);
        if (!position) return attachment;
        editor.editor.setSelectedRange(position); editor.editor.deleteInDirection('forward');
      } else editor.editor.setSelectedRange(range);
      editor.editor.insertAttachment(replacement);
      if (attachment) editor.editor.setSelectedRange(range);
      return replacement;
    };
    const edit = attachment => {
      const current = attachment ? details(attachment) : {};
      const range = editor.editor.getSelectedRange();
      editImage({...current, inline: true, upload, apply: result => {
        replace(attachment, result, range);
        render();
      }});
    };
    const installButton = () => {
      const attach = editor.toolbarElement?.querySelector('[data-trix-action="attachFiles"]');
      if (!attach || editor.toolbarElement.querySelector('[data-blog-image-insert]')) return;
      const insert = button('insert', () => edit(null));
      insert.className = 'trix-button trix-button--icon'; insert.dataset.blogImageInsert = 'true'; insert.title = t('insert');
      insert.setAttribute('aria-label', t('insert')); insert.tabIndex = -1;
      // Keep the same button size, colour and spacing as Trix's attachment tools.
      insert.innerHTML = '<svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" style="position:absolute;inset:0;margin:auto"><rect x="3" y="3" width="18" height="18" rx="2" fill="none" stroke="currentColor" stroke-width="2"/><circle cx="8" cy="8" r="2" fill="currentColor"/><path d="M4 18l5-6 4 4 3-4 5 6" fill="none" stroke="currentColor" stroke-width="2"/></svg>';
      insert.addEventListener('mousedown', event => event.preventDefault());
      attach.after(insert);
    };
    editor.after(list);
    const render = () => {
      if (!editor.editor) return;
      list.replaceChildren();
      editor.editor.getDocument().getAttachments().forEach(attachment => {
        const current = details(attachment); if (!current?.url) return;
        const item = button('edit', () => edit(attachment)); const image = document.createElement('img');
        image.src = current.url; image.alt = current.alt; image.style.cssText = 'width:80px;height:60px;object-fit:contain;display:block';
        item.prepend(image); list.append(item);
      });
    };
    editor.addEventListener('trix-change', render);
    editor.addEventListener('trix-initialize', () => { installButton(); render(); });
    installButton(); render();
    imageControls(editor, {details, replace, edit});
  });
}
